import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { easeOut } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useSourceAnalyticActions } from '@atlassian/ai-smart-button-api';
import { useIsNudgeVisible } from '@atlassian/ai-smart-button-api/state';
import { OnboardingEnablement } from '@atlassian/conversation-assistant/controllers/chat-onboarding';
import {
	type ConversationAssistantProps,
	type ConversationAssistantConfig,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant/types';
import type { ConversationActionTags } from '@atlassian/rovo-conversation-actions/types';
import {
	useRovoExperienceAPIStore,
	type RovoExperienceSidebarAPI,
} from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { AISmartButtonProvider } from '../../common/context';
import { useDebugNudgeTriggers } from '../../common/hooks/use-debug-nudge-triggers';
import { markUnreadCacheStale } from '../../common/hooks/use-unread-conversation-status';
import type { AISmartButtonProps } from '../../common/types/shared-types';
import { isBrowser } from '../../common/utils/is-browser';
import { useRovoButtonEventsInstrumentation } from '../../hooks/useRovoButtonEventsInstrumentation';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIFullScreenModal } from '../ai-full-screen-modal';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIMiniModal, preloadAIMiniModal } from '../ai-mini-modal';

import { AISmartButtonUI } from './components/ai-smart-button-ui';
import { selectionCountsInMainEditor } from './utils/pmSelectionCounts';

const ANIMATION_DURATION = 150;

const styles = cssMap({
	wrapper: { position: 'relative' },
	animatedMiniModalBefore: {
		transformOrigin: 'right bottom',
		visibility: 'hidden',
		opacity: token('opacity.disabled'),
		transition: `transform ${ANIMATION_DURATION}ms ${easeOut}, opacity ${ANIMATION_DURATION}ms ${easeOut}, visibility ${ANIMATION_DURATION}ms ${easeOut}, filter ${ANIMATION_DURATION}ms ${easeOut}`,
		transform: 'scale(0)',
		filter: 'blur(8px)',
		willChange: 'transform, opacity, visibility, filter',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'all 1ms',
		},
	},
	animatedMiniModalAfter: {
		opacity: 1,
		transform: 'scale(1)',
		visibility: 'visible',
		filter: 'blur(0px)',
	},
});

export const AISmartButton: React.ForwardRefExoticComponent<
	Omit<ConversationAssistantProps, 'config'> & {
		cloudId: string;
		conversationAssistantConfig?: ConversationAssistantConfig;
		isNav4Enabled: boolean;
		isSidebarAvailable?: boolean;
		orgId?: string;
		overrideMiniModalWrapper?: (children: React.ReactNode) => React.ReactNode;
		product: string;
		sidebarApi: RovoExperienceSidebarAPI;
		sitePickerRender?: React.JSX.Element;
	} & {
		analyticsAttributes?: Record<string, string>;
		inProductExperience?: string;
		isDisabled?: boolean;
		isLiveViewMode?: boolean;
		onAnchorElementReady?: (elementId: string) => void;
		pageMode?: string;
		productPage: string;
		tags?: ConversationActionTags;
	} & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, AISmartButtonProps>(
	({ isDisabled = false, ...contextProps }, forwardedRef) => {
		const { product, sidebarApi, productPage, analyticsAttributes } = contextProps;
		const hoverTimerRef = React.useRef<NodeJS.Timeout | null>(null);
		const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
		const { setButtonClickedTimestamp } = useSourceAnalyticActions();
		const {
			mode,
			toggleConversationAssistantUIMode,
			openConversationAssistantUIMode,
			setIsMiniModalAvailable,
			setIsFullScreenModalAvailable,
			setOpenWithChatHistory,
		} = useRovoExperienceAPIStore();
		const prevModeRef = useRef<typeof mode>(mode);

		const isFullScreenModalEnabled =
			product === 'jira' &&
			fg('asx_full_screen_modal_mode') &&
			FeatureGates.getExperimentValue<boolean>('ai_agent_sessions_experience', 'isEnabled', false, {
				fireExperimentExposure: false,
			});
		const isFullScreenModalExperience =
			mode === ConversationAssistantUIModes.FULL_SCREEN_MODAL && isFullScreenModalEnabled;

		const [rovoButtonCanRender, setRovoButtonCanRender] = useState(
			mode !== ConversationAssistantUIModes.MINI_MODAL && !isFullScreenModalExperience,
		);
		const [hasStartedPreloading, setHasStartedPreloading] = useState(false);

		// Ref to the button wrapper. Used by the proximity handler below to
		// measure the button's screen-space center for distance calculations.
		const proximityWrapperRef = useRef<HTMLDivElement | null>(null);

		// Ref mirror of `rovoButtonCanRender` so the proximity handler — which
		// runs from inside a useEffect that doesn't re-subscribe on render —
		// can see the current visibility state without consuming the animation
		// budget while the button is hidden (mini-modal mode). Updated by a
		// tiny sync-effect below.
		const canRenderRef = useRef(rovoButtonCanRender);

		// Mouse-proximity animation enablement (TREX-1527). Read from the
		// `rovo_button_mouse_tracking_animation` Statsig experiment on every
		// render — FeatureGates dedupes exposure logging per session, so this
		// is safe and naturally handles the case where the SDK hadn't fully
		// initialised when AISmartButton first rendered (the next render
		// picks up the live value). Matches the pattern used by
		// `isUnreadIndicatorEnabled` in AISmartButtonUI.
		//
		// When false (control / not bucketed / Statsig unreachable), the
		// animated logo is never rendered — AIButton falls back to the
		// existing static RovoColorLogo. This gates the *visual*; the trigger
		// logic is gated separately inside the effect below.
		const proximityAnimationEnabled = FeatureGates.getExperimentValue<boolean>(
			'rovo_button_mouse_tracking_animation',
			'isEnabled',
			false,
		);

		// Mouse-proximity animation key (TREX-1527). Bumped each time the
		// cursor crosses INTO the proximity radius, which remounts the
		// animated Rovo SVG and restarts its 2.5s SMIL color-cycle.
		// Starts at `undefined` so that on first mount AIButton renders the
		// static fallback. The proximity handler sets it to 0 on the FIRST
		// in-radius cross, which triggers the animated logo's first SMIL run,
		// and bumps it on every subsequent entry while `repeatable = true`.
		const [proximityAnimationKey, setProximityAnimationKey] = useState<number | undefined>(
			undefined,
		);

		// Refs that survive proximity-effect re-runs (TREX-1606).
		//
		// Previously these were closure-scoped `let` variables INSIDE the
		// proximity effect. Any time the effect tore down and re-ran they
		// silently reset to `false`, which produced two visible bugs:
		//
		//   1. `wasInRadius` losing its `true` state caused the edge-detector
		//      (`isInRadius && !wasInRadius`) to miss the next entry into the
		//      radius — the user had to physically move OUT and back IN
		//      before the animation would fire again.
		//   2. `proximityHasFired` losing its `true` state caused the
		//      `repeatable=false` mode to fire more than once per page, and
		//      could also cause "first-fire per session" tracking to lie.
		//
		// Promoting to refs means the values persist for the lifetime of the
		// component instance, independent of how many times the effect
		// re-binds its listener. The route-change reset effect below
		// explicitly clears them when the user navigates.
		const proximityHasFiredRef = useRef(false);
		const wasInRadiusRef = useRef(false);

		// Ref mirror of `proximityAnimationEnabled` (TREX-1606).
		//
		// The proximity effect previously had `proximityAnimationEnabled` in
		// its dep array. Because the gate value is read fresh on every render
		// via `FeatureGates.getExperimentValue`, the value can flip
		// `false → true` shortly after mount (when the Statsig SDK finishes
		// initialising and returns the bucketed value instead of the default
		// `false` fallback). That dep-array flip caused the effect to tear
		// down and re-run, blowing away the per-session proximity state.
		//
		// We now mirror the value into a ref and read it inside the effect.
		// The effect itself runs exactly once per mount with an empty dep
		// array, which is the behaviour the original comment claimed but the
		// original dep array did not deliver.
		const proximityAnimationEnabledRef = useRef(proximityAnimationEnabled);

		// Keep `canRenderRef` in sync with `rovoButtonCanRender` so the
		// long-lived proximity effect (which only subscribes once on mount)
		// can read the current value without re-running.
		useEffect(() => {
			canRenderRef.current = rovoButtonCanRender;
		}, [rovoButtonCanRender]);

		// Keep `proximityAnimationEnabledRef` in sync with the latest gate
		// value. Lets the proximity effect early-bail without re-running when
		// the gate is off.
		useEffect(() => {
			proximityAnimationEnabledRef.current = proximityAnimationEnabled;
		}, [proximityAnimationEnabled]);

		// Reset per-page proximity state when the user navigates between
		// pages (TREX-1606). `productPage` is a stable identifier for the
		// current page surface — when it changes, the user has moved to a
		// new page, so:
		//   - `proximityHasFiredRef` is reset so `repeatable=false` users
		//     can see the animation again on the new page (matches the
		//     "once per page" intent in the effect comment).
		//   - `wasInRadiusRef` is reset so the next mousemove correctly
		//     re-evaluates whether the cursor is in the radius for the
		//     newly-positioned button.
		//   - `proximityAnimationKey` is reset so the AISmartButtonUI
		//     re-renders with the static fallback at first, then transitions
		//     to the animated logo on the next radius entry — preserving the
		//     "static at rest" UX on every fresh page.
		useEffect(() => {
			proximityHasFiredRef.current = false;
			wasInRadiusRef.current = false;
			setProximityAnimationKey(undefined);
		}, [productPage]);

		// Proximity animation suppression while a nudge is visible (TREX-1601).
		//
		// `AIProactiveNudge` publishes its visibility into the shared
		// `AISmartButtonStore`; we subscribe via `useIsNudgeVisible` and
		// mirror it into a ref so the mount-once proximity effect can read
		// the current value on every mousemove without re-binding its
		// listener. Mirrors the `canRenderRef` pattern just above.
		//
		// Suppressing the animation while a nudge is on screen avoids two
		// visual effects (the nudge bubble and the color-cycle on the
		// button itself) competing for the same surface, which is jarring
		// and dilutes the impact of both.
		const isNudgeVisible = useIsNudgeVisible();
		const isNudgeVisibleRef = useRef(isNudgeVisible);
		useEffect(() => {
			isNudgeVisibleRef.current = isNudgeVisible;
		}, [isNudgeVisible]);

		useDebugNudgeTriggers();

		useEffect(() => {
			setIsMiniModalAvailable(true);
			if (isFullScreenModalEnabled) {
				setIsFullScreenModalAvailable?.(true);
			}

			if (!isBrowser()) {
				return;
			}

			// Start preloading when mouse is within the bottom-right box (300px from bottom, 300px from right)
			const handleMouseMove = (event: MouseEvent) => {
				if (isBrowser() && !hasStartedPreloading) {
					const viewportHeight = window.innerHeight;
					const viewportWidth = window.innerWidth;
					const mouseX = event.clientX;
					const mouseY = event.clientY;

					const distanceFromBottom = viewportHeight - mouseY;
					const distanceFromRight = viewportWidth - mouseX;

					if (distanceFromBottom <= 300 && distanceFromRight <= 300) {
						preloadAIMiniModal();
						setHasStartedPreloading(true);
					}
				}
			};

			const unbind = bindAll(window, [
				{
					type: 'mousemove',
					listener: handleMouseMove,
				},
			]);

			return () => {
				setIsMiniModalAvailable(false);
				if (isFullScreenModalEnabled) {
					setIsFullScreenModalAvailable?.(false);
				}
				isBrowser() && unbind();
			};
		}, [
			setIsMiniModalAvailable,
			setIsFullScreenModalAvailable,
			hasStartedPreloading,
			isFullScreenModalEnabled,
		]);

		// --- Mouse-proximity animation (TREX-1527, hardened in TREX-1606) ---
		// This effect is intentionally mount-once / unmount-once: the dep
		// array is empty. All values that can change between renders
		// (the enabled gate, the render-eligibility flag, the per-trigger
		// state) are read through refs that are kept in sync above so the
		// effect never has to tear down and re-run. Previously this effect
		// depended on `proximityAnimationEnabled`, which flipped from `false`
		// (Statsig SDK not yet initialised) to `true` (bucketed value
		// resolved) shortly after mount and silently reset the per-page
		// proximity state — see TREX-1606 for the full investigation.
		//
		// IMPORTANT: the listener is registered unconditionally. The
		// "is the experiment enabled?" check is deliberately inside
		// `updateProximity` rather than at the top of the effect. Gating
		// the registration here would mean a user whose Statsig SDK
		// resolves AFTER mount (i.e. `proximityAnimationEnabled` was
		// `false` at the moment the effect ran but became `true` shortly
		// after) never gets a listener at all — because the mount-once
		// effect doesn't re-run. Bailing inside the handler is virtually
		// free (a ref read + early return on each mousemove for
		// non-bucketed users) and is consistent with how `canRenderRef` is
		// already handled.
		//
		// The Statsig param reads (`PROXIMITY_RADIUS_PX`, `PROXIMITY_REPEAT`)
		// remain inside the effect closure so they capture the value at
		// mount time. This is intentional: mid-session experiment-param
		// changes should NOT silently change the radius or repeat behaviour
		// for a user already on a page — they take effect on the next mount.
		useEffect(() => {
			if (!isBrowser()) {
				return;
			}

			// Tunable from Switcheroo without redeploying:
			//   proximityRadiusPx (number)  — radius around the button in pixels.
			//   repeatable        (boolean) — true: replay on every entry into radius.
			//                                  false: play once per page mount.
			const PROXIMITY_RADIUS_PX = FeatureGates.getExperimentValue<number>(
				'rovo_button_mouse_tracking_animation',
				'proximityRadiusPx',
				150,
			);
			const PROXIMITY_REPEAT = FeatureGates.getExperimentValue<boolean>(
				'rovo_button_mouse_tracking_animation',
				'repeatable',
				false,
			);

			let lastMouseX = -1;
			let lastMouseY = -1;
			let rafId: number | undefined;

			const updateProximity = () => {
				rafId = undefined;
				// Bail if the experiment isn't enabled for this user. Read
				// fresh each frame via the ref so users whose Statsig SDK
				// resolves after mount still get the animation once the
				// flag flips `true`. See effect header for the full
				// rationale.
				if (!proximityAnimationEnabledRef.current) {
					return;
				}
				// Don't measure or fire while the button is not actually
				// being rendered (e.g. mini-modal mode). Prevents the
				// once-per-page animation budget from being consumed
				// invisibly.
				if (!canRenderRef.current) {
					return;
				}
				// Suppress the animation while a nudge bubble is on screen
				// (TREX-1601). Skipping the entry — rather than queuing it
				// to fire after the nudge dismisses — is deliberate: the
				// animation is meant to react to live cursor approach, not
				// to replay a stale gesture once the screen is clear. The
				// suppression behaviour ships as part of the same
				// experiment as the proximity animation itself.
				//
				// We reach this line only when `proximityAnimationEnabledRef`
				// (read via `getExperimentValue`) is true, so the user is
				// already bucketed into the experiment — no separate gate
				// check is needed for the decision. We still call `fg(...)`
				// for its exposure-tracking side effect to satisfy the
				// `@atlaskit/platform/no-preconditioning` ESLint rule and
				// keep Statsig's exposure metric attributable to the
				// suppression code path. Calling `fg(...)` for side effect
				// (rather than using its return value as the gate) avoids
				// the publisher/consumer SDK mismatch we hit in TREX-1601
				// local testing — see ai-proactive-nudge/main.tsx for the
				// matching publisher fix.
				if (isNudgeVisibleRef.current) {
					fg('rovo_button_mouse_tracking_animation');
					return;
				}
				const el = proximityWrapperRef.current;
				if (!el) {
					return;
				}
				const rect = el.getBoundingClientRect();
				const centerX = rect.left + rect.width / 2;
				const centerY = rect.top + rect.height / 2;
				const dx = lastMouseX - centerX;
				const dy = lastMouseY - centerY;
				const distance = Math.hypot(dx, dy);
				const isInRadius = distance <= PROXIMITY_RADIUS_PX;

				// Detect "entered the radius" → mount (first entry) or remount
				// (subsequent entries when repeatable=true) the animated Rovo
				// SVG by setting/bumping the React key. This is the only place
				// we touch React state from the mousemove handler.
				//
				// Respect `prefers-reduced-motion: reduce` (WCAG 2.3.3): users
				// who have requested reduced motion should not see the
				// color-cycle animation even when bucketed into the test
				// cohort. We bail BEFORE mutating React state so the static
				// RovoColorLogo fallback continues to render. Re-evaluated
				// per-trigger (cheap) so that if the user changes their OS
				// setting mid-session it takes effect on the next entry.
				if (isInRadius && !wasInRadiusRef.current) {
					const prefersReducedMotion =
						typeof window !== 'undefined' &&
						typeof window.matchMedia === 'function' &&
						window.matchMedia('(prefers-reduced-motion: reduce)').matches;
					if (!prefersReducedMotion && (PROXIMITY_REPEAT || !proximityHasFiredRef.current)) {
						setProximityAnimationKey((k) => (k === undefined ? 0 : k + 1));
						proximityHasFiredRef.current = true;
					}
				}
				wasInRadiusRef.current = isInRadius;
			};

			const handleProximityMouseMove = (event: MouseEvent) => {
				lastMouseX = event.clientX;
				lastMouseY = event.clientY;
				// Only schedule a new rAF if one isn't already pending. Track the
				// rAF ID so we can cancel it in the cleanup — without this, a
				// pending rAF would still fire after unmount and call
				// setProximityAnimationKey on an unmounted component (a React
				// warning in dev, a potential no-op state update in prod that
				// breaks React StrictMode double-invocation invariants).
				if (rafId === undefined) {
					rafId = requestAnimationFrame(updateProximity);
				}
			};

			const unbind = bindAll(window, [
				{
					type: 'mousemove',
					listener: handleProximityMouseMove,
				},
			]);

			return () => {
				if (rafId !== undefined) {
					cancelAnimationFrame(rafId);
				}
				unbind();
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps -- See header comment: effect deliberately runs once per mount and reads
			// proximityAnimationEnabledRef / canRenderRef / wasInRadiusRef /
			// proximityHasFiredRef / proximityWrapperRef through refs that
			// are kept in sync above (or, in the case of proximityWrapperRef,
			// attached to the rendered wrapper element via ref={...}).
		}, []);

		useEffect(() => {
			fireRovoButtonUIEvent({
				source: productPage,
				action: 'rendered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiSmartButton',
				attributes: {
					product,
					productPage,
					...analyticsAttributes,
					version: 'refresh',
					legacy: false,
				},
				product,
			});
		}, [fireRovoButtonUIEvent, product, productPage, analyticsAttributes]);

		useEffect(() => {
			// Wait for the closing animation of modal to complete before showing the rovo button.
			// The timer must be cleared on cleanup; otherwise rapid mode transitions
			// (e.g. undefined → sidebar → mini-modal) leave stale timers that fire after
			// ANIMATION_DURATION and incorrectly set rovoButtonCanRender to true,
			// unmounting the AIMiniModal even though mode is still 'mini-modal'.
			if (mode !== ConversationAssistantUIModes.MINI_MODAL && !isFullScreenModalExperience) {
				const timerId = setTimeout(() => {
					setRovoButtonCanRender(true);
				}, ANIMATION_DURATION);
				return () => {
					clearTimeout(timerId);
				};
			}

			setRovoButtonCanRender(false);
		}, [mode, setRovoButtonCanRender, isFullScreenModalExperience]);

		useEffect(() => {
			if (
				mode === undefined &&
				(prevModeRef.current === ConversationAssistantUIModes.MINI_MODAL ||
					prevModeRef.current === ConversationAssistantUIModes.SIDEBAR ||
					(prevModeRef.current === ConversationAssistantUIModes.FULL_SCREEN_MODAL &&
						fg('agent_session_experience_fg')))
			) {
				// AISmartButtonUI is unmounted while mini-modal, sidebar, or full-screen-modal is open, so the hook
				// cannot watch mode internally. Mark cache stale here so the button remounts with
				// stale data visible while the background re-fetch completes.
				markUnreadCacheStale();
			}
			prevModeRef.current = mode;
		}, [mode]);

		const onAISmartButtonClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
				// Set timestamp immediately on click
				setButtonClickedTimestamp(Date.now());

				// Plain button opens the default (suggestions) view — ensure a prior pill
				// click can never leak its open-with-chat-history intent into this open.
				setOpenWithChatHistory?.(false);

				toggleConversationAssistantUIMode({
					targetMode: ConversationAssistantUIModes.MINI_MODAL,
					sidebarApi,
				});

				const selectionAttributes: { totalSelectedChars?: number; totalSelectedWords?: number } =
					{};
				if (fg('platform_editor_ai_aifc_streaming')) {
					const counts = selectionCountsInMainEditor(e.currentTarget);
					if (counts != null) {
						selectionAttributes.totalSelectedWords = counts.words;
						selectionAttributes.totalSelectedChars = counts.chars;
					}
				}

				fireRovoButtonUIEvent({
					source: productPage,
					action: 'clicked',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoSmartButton',
					attributes: {
						product,
						productPage,
						...analyticsAttributes,
						...selectionAttributes,
						version: 'refresh',
					},
					product,
				});

				setRovoButtonCanRender(false);
			},
			[
				product,
				productPage,
				analyticsAttributes,
				fireRovoButtonUIEvent,
				toggleConversationAssistantUIMode,
				sidebarApi,
				setButtonClickedTimestamp,
				setOpenWithChatHistory,
			],
		);

		const onChatHistoryClick = React.useCallback(() => {
			setOpenWithChatHistory?.(true);

			openConversationAssistantUIMode({
				targetMode: ConversationAssistantUIModes.MINI_MODAL,
				sidebarApi,
			});

			fireRovoButtonUIEvent({
				source: productPage,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoSmartButton',
				attributes: {
					product,
					productPage,
					...analyticsAttributes,
					version: 'refresh',
					openedWithChatHistory: true,
				},
				product,
			});

			setRovoButtonCanRender(false);
		}, [
			product,
			productPage,
			analyticsAttributes,
			fireRovoButtonUIEvent,
			openConversationAssistantUIMode,
			sidebarApi,
			setOpenWithChatHistory,
		]);

		const onAISmartButtonEnter = React.useCallback(() => {
			hoverTimerRef.current = setTimeout(() => {
				fireRovoButtonUIEvent({
					source: productPage,
					action: 'hovered',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiSmartButton',
					attributes: {
						product,
						productPage,
						...analyticsAttributes,
						version: 'refresh',
					},
					product,
				});
			}, 1000);
		}, [fireRovoButtonUIEvent, product, productPage, analyticsAttributes]);

		const onAISmartButtonLeave = React.useCallback(() => {
			if (hoverTimerRef.current) {
				clearTimeout(hoverTimerRef.current);
				hoverTimerRef.current = null;
			}
		}, []);

		return (
			<>
				<AISmartButtonProvider {...contextProps}>
					<Box xcss={styles.wrapper} ref={proximityWrapperRef}>
						<Box
							xcss={cx(
								styles.animatedMiniModalBefore,
								mode === ConversationAssistantUIModes.MINI_MODAL && styles.animatedMiniModalAfter,
							)}
						>
							{!sidebarApi.isSidebarOpen() &&
								!rovoButtonCanRender &&
								!isFullScreenModalExperience && <AIMiniModal />}
						</Box>
						{!sidebarApi.isSidebarOpen() && rovoButtonCanRender && (
							<AISmartButtonUI
								ref={forwardedRef}
								onClick={onAISmartButtonClick}
								onChatHistoryClick={onChatHistoryClick}
								isDisabled={isDisabled}
								onMouseEnter={onAISmartButtonEnter}
								onMouseLeave={onAISmartButtonLeave}
								proximityAnimationKey={proximityAnimationKey}
							/>
						)}
					</Box>
					{!sidebarApi.isSidebarOpen() && isFullScreenModalExperience && <AIFullScreenModal />}
				</AISmartButtonProvider>
				<OnboardingEnablement product={product} />
			</>
		);
	},
);
